import { useCallback } from "react"

import { concat, Observable, of } from "rxjs"
import { catchError, map } from "rxjs/operators"

import handleAPIError from "../../utils/handleAPIError"
import ApiResult from "../ApiResult"

type ApiHandlerOptions<T> = {
  observableFactory: () => Observable<T>
  onSuccess?: (result: T) => void
  onLoadingChange?: (loading: boolean) => void
  defaultSuccessMessage?: string
  setResponse?: (message: string) => void
}

/**
 * A hook for handling API calls. It takes an options object with:
 *  - `observableFactory`: A function that returns an Observable for the API call.
 *  - `onSuccess`: An optional function to call when the API call is successful.
 *  - `onLoadingChange`: An optional function to call when the loading state changes.
 *  - `defaultSuccessMessage`: An optional string to display when the API call is successful and no other message is provided.
 *  - `setResponse`: An optional function to call to set the response message.
 * It returns a function that takes the options object and returns an unsubscribe function.
 * The returned function should be called with the options object whenever the API call needs to be made.
 * The unsubscribe function should be called when the component is unmounted to prevent memory leaks.
 */
export function useApiObservableHandler<T>() {
  return useCallback((options: ApiHandlerOptions<T>) => {
    const { observableFactory, onSuccess, onLoadingChange, defaultSuccessMessage, setResponse } = options

    const observable = concat(
      of({ loading: true }),
      observableFactory().pipe(
        map((result) => ({ loading: false, result, error: null })),
        catchError((error) => of({ loading: false, result: undefined, error }))
      )
    )

    const subscription = observable.subscribe(({ loading, result, error }: ApiResult<T>) => {
      onLoadingChange?.(loading)

      if (!loading && !error && result !== undefined) {
        setResponse?.((result as unknown as string) || defaultSuccessMessage || "")
        onSuccess?.(result)
      }

      if (error) {
        handleAPIError(error)
      }
    })

    return () => subscription.unsubscribe()
  }, [])
}
